import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from './utils.js';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: ReactNode;
  description?: ReactNode;
  /** Boxed row (bordered card-like). */
  boxed?: boolean;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, boxed = false, className, id, ...rest },
  ref,
) {
  const control = (
    <input
      {...rest}
      ref={ref}
      id={id}
      type="checkbox"
      className={cx(
        'size-4 shrink-0 rounded border-slate-300 text-[color:var(--admin-accent,#2563EB)] accent-[color:var(--admin-accent,#2563EB)] focus:ring-[color:var(--admin-accent,#2563EB)] disabled:opacity-50 dark:border-slate-600',
        !label && !description ? className : undefined,
      )}
    />
  );

  if (!label && !description) {
    return control;
  }

  return (
    <label
      htmlFor={id}
      className={cx(
        'flex items-start gap-3 text-sm text-slate-800 dark:text-slate-200',
        boxed && 'rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2.5',
        className,
      )}
      data-ui="checkbox"
    >
      <span className="mt-0.5">{control}</span>
      <span className="min-w-0">
        {label ? <span className="block font-medium">{label}</span> : null}
        {description ? <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">{description}</span> : null}
      </span>
    </label>
  );
});
